"use client";

import { ExternalLink, Zap } from "lucide-react";
import { Switch } from "@repo/ui";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import type { TriggerableFlow } from "@/hooks/use-flow-by-trigger";
import { cn } from "@/lib/utils";

const plural = (count: number) => `${count} flow${count === 1 ? "" : "s"}`;

/**
 * "⚡ N flows", listing them by name on hover. Each opens the flow editor in a
 * new tab so checking what a mutation would set off never loses the selection.
 */
export function FlowTriggerBadge({
  flows,
  className,
}: {
  flows: TriggerableFlow[];
  className?: string;
}) {
  if (flows.length === 0) return null;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            "inline-flex cursor-default items-center gap-1 whitespace-nowrap underline decoration-dotted underline-offset-2",
            className,
          )}
        >
          <Zap className="size-3 text-yellow-500" />
          {plural(flows.length)}
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-64 p-2">
        <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          Will trigger
        </p>
        <div className="flex flex-col">
          {flows.map((flow) => (
            <a
              key={flow.id}
              href={`/p/flows/${flow.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm text-gray-900 hover:bg-gray-100"
            >
              <Zap className="size-3.5 shrink-0 text-yellow-500" />
              <span className="truncate">{flow.name}</span>
              <ExternalLink className="ml-auto size-3 shrink-0 text-gray-400" />
            </a>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Whether the mutation runs the entity's flows. On by default — reads as
 * "flows will fire, switch off to skip", which beats a double negative.
 */
export function FlowTriggerSwitch({
  flows,
  checked,
  onCheckedChange,
  className,
}: {
  flows: TriggerableFlow[];
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}) {
  if (flows.length === 0) return null;

  return (
    <div className={cn("flex items-center gap-2 text-xs text-gray-500", className)}>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
      Trigger <FlowTriggerBadge flows={flows} />
    </div>
  );
}

/** Read-only counterpart, for levels that can't opt out (nested sub-forms). */
export function FlowTriggerHint({
  flows,
  className,
}: {
  flows: TriggerableFlow[];
  className?: string;
}) {
  if (flows.length === 0) return null;

  return (
    <div className={cn("flex items-center gap-1 text-xs text-gray-500", className)}>
      This will trigger <FlowTriggerBadge flows={flows} />
    </div>
  );
}
